// External libraries
import type { FocusEvent, KeyboardEvent } from 'react';
import React, { useRef, useState } from 'react';
import { useStoreState } from '@/store';
//@ts-expect-error untyped import
import { QuestionIcon, Tooltip } from '@checkout/react-components';

// Internal modules
import {
  features,
  getContent,
  isEmeaPasswordlessWithoutCookiesCountry,
} from '@/lib';
import { LegalAgeDisclosure } from '../ContactInformation';
import { SectionHeading, SectionSubHeading } from '../Headings';
import { QuestionButton, TipsList } from './StyledComponents';
import { getTooltipStyleOverrides } from './styles';
import { getPomaTooltipRenderPosition } from './utils';
import { getTermsLinks } from '../OnboardTerms/Components';
import { Paragraph } from '@/components/Common';

const POMAHeader = (): JSX.Element => {
  const activeSCAPhoneConfirmation = features.isActive(
    'shouldShowSCAPhoneConfirmation',
  );
  return (
    <div data-testid="POMAHeader">
      {activeSCAPhoneConfirmation && <LegalAgeDisclosure />}
      <SectionHeading
        marginTop={'1.5rem'}
        textAlign={activeSCAPhoneConfirmation ? 'left' : 'center'}
      >
        {getContent('terms.pomaHeader')}
      </SectionHeading>

      <SectionSubHeading fontSize="14">
        <PomaTermsWithOneTouch />
      </SectionSubHeading>
    </div>
  );
};

const PomaTermsWithOneTouch = (): JSX.Element => {
  const webLLSTreatment = features.isActive('isWebLLS');

  let contentKey = features.isActive('passwordlessForcedSignup')
    ? 'terms.passwordlessForcedSignup'
    : 'terms.pomaSimplified';

  if (webLLSTreatment) {
    contentKey = 'terms.signup';
  }

  const isSubmitting = useStoreState(({ ui }) => ui.isSubmitting);
  const termsLinks = getTermsLinks(contentKey, isSubmitting);

  const content = getContent(contentKey, termsLinks, {
    useHTML: !webLLSTreatment,
  });

  return <PasswordlessTooltip content={content} />;
};

const PasswordlessTooltip = ({
  content,
}: {
  content: React.ReactNode;
}): JSX.Element => {
  const webLLSTreatment = features.isActive('isWebLLS');
  const isEmeaNoCookies = isEmeaPasswordlessWithoutCookiesCountry();
  const questionButtonRef = useRef<HTMLButtonElement>(null);
  const tooltipContentRef = useRef<HTMLUListElement>(null);
  const [isDisplayTooltip, setIsDisplayTooltip] = useState(false);

  // Decide the offset of tooltip based on poma terms height and
  // height of tooltip itself
  const positioningProps = getPomaTooltipRenderPosition({
    questionButtonRef,
    tooltipContentRef,
  });
  const tooltipStyleOverrides = getTooltipStyleOverrides({
    isDisplayTooltip,
    ...positioningProps,
  });

  const QuestionButtonBlurHandler = (
    e: FocusEvent<HTMLButtonElement>,
  ): void => {
    setIsDisplayTooltip(false);
  };
  const QuestionButtonKeyDownHandler = (
    e: KeyboardEvent<HTMLButtonElement>,
  ): void => {
    // on Space or Enter press
    // @ts-expect-error similar error as infoButtonKeyDownHandler in Email.tsx
    // TODO: TS says this should be string key numbers
    if (e.key === 32 || e.key === 13) {
      e.preventDefault();
      setIsDisplayTooltip(!isDisplayTooltip);
    }
  };
  const QuestionButtonClickHandler = (
    e: React.MouseEvent<HTMLElement>,
  ): void => {
    setIsDisplayTooltip(!isDisplayTooltip);
  };
  return (
    <>
      <Paragraph isTermsContent testID="POMATerms">
        {content}
        {!webLLSTreatment && !isEmeaNoCookies && (
          <QuestionButton
            aria-describedby="questionTooltip"
            data-testid="simplifiedPomaTooltipButton"
            onBlur={QuestionButtonBlurHandler}
            onClick={QuestionButtonClickHandler}
            onKeyDown={QuestionButtonKeyDownHandler}
            ref={questionButtonRef}
            type="button"
          >
            <span>
              <QuestionIcon
                title={getContent('base.moreInfo')}
                type="primary"
              />
            </span>
          </QuestionButton>
        )}
      </Paragraph>
      <Tooltip arrowPosition="bottom" styleOverrides={tooltipStyleOverrides}>
        <TipsList
          id="questionTooltip"
          data-testid="questionTooltip"
          ref={tooltipContentRef}
        >
          <li>{getContent('poma.tooltip.noPassword')}</li>
          <li>{getContent('poma.tooltip.speedCheckout')}</li>
          <li>{getContent('poma.tooltip.inControl')}</li>
          <li>{getContent('poma.tooltip.security')}</li>
        </TipsList>
      </Tooltip>
    </>
  );
};

export { POMAHeader, PomaTermsWithOneTouch, PasswordlessTooltip };
